import { forwardRef, useEffect, useState } from 'react';

const Hero = forwardRef<HTMLElement>((props, ref) => {
  const [scrollY, setScrollY] = useState(0);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 120);
    return () => clearTimeout(t);
  }, []);

  const parallaxOffset = scrollY * 0.3;

  const pillars = [
    { label: 'Media Mastery', sub: 'Command every lens' },
    { label: 'Executive Presence', sub: 'Own the room' },
    { label: 'Message Architecture', sub: 'Words that move nations' },
    { label: 'High-Stakes Briefing', sub: 'No second chances' },
  ];

  return (
    <section ref={ref} className="relative h-screen w-full overflow-hidden bg-[#0D0C0A]">
      {/* Background */}
      <div
        className="absolute inset-0 bg-cover bg-center will-change-transform"
        style={{
          backgroundImage:
            'url(https://public.readdy.ai/ai/img_res/de3821e0fe5bb1fbb40c2d16993ea9da.jpg)',
          transform: `translateY(${parallaxOffset}px) scale(1.12)`,
          transition: 'transform 0.05s linear',
        }}
      />

      {/* Layered overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/35 to-black/90" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-black/40" />
      <div className="absolute bottom-0 left-0 right-0 h-[220px] md:h-[220px] bg-gradient-to-t from-[#0D0C0A] to-transparent" />

      {/* Main content */}
      <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4 md:px-8 w-full pb-[140px] md:pb-0">

        {/* ── CREST / EMBLEM ── */}
        <div
          className="flex flex-col items-center mb-6 md:mb-8"
          style={{
            opacity: loaded ? 1 : 0,
            transform: loaded ? 'translateY(0)' : 'translateY(-20px)',
            transition: 'opacity 1.2s ease 0.2s, transform 1.2s ease 0.2s',
          }}
        >
          {/* Top rule */}
          <div className="flex items-center gap-3 md:gap-4 mb-4 md:mb-5">
            <div className="w-[50px] md:w-[80px] h-[1px] bg-gradient-to-r from-transparent to-[#C9A96E]/70" />
            <div className="flex items-center gap-[4px] md:gap-[5px]">
              <div className="w-[2px] h-[2px] bg-[#C9A96E]/50 rotate-45" />
              <div className="w-[3px] md:w-[4px] h-[3px] md:h-[4px] bg-[#C9A96E]/70 rotate-45" />
              <div className="w-[2px] h-[2px] bg-[#C9A96E]/50 rotate-45" />
            </div>
            <div className="w-[50px] md:w-[80px] h-[1px] bg-gradient-to-l from-transparent to-[#C9A96E]/70" />
          </div>

          {/* Crest emblem — geometric sovereign mark */}
          <div className="relative flex items-center justify-center mb-4 md:mb-5" style={{ width: '52px', height: '52px' }}>
            {/* Outer octagon ring */}
            <div
              className="absolute inset-0 border border-[#C9A96E]/40"
              style={{ borderRadius: '2px', transform: 'rotate(22.5deg)' }}
            />
            <div
              className="absolute border border-[#C9A96E]/20"
              style={{ inset: '5px', borderRadius: '2px', transform: 'rotate(0deg)' }}
            />
            {/* Inner diamond */}
            <div
              className="absolute bg-[#C9A96E]/10 border border-[#C9A96E]/60"
              style={{ width: '20px', height: '20px', transform: 'rotate(45deg)' }}
            />
            {/* Center dot */}
            <div className="absolute w-[4px] h-[4px] bg-[#C9A96E] rounded-full" style={{ boxShadow: '0 0 8px rgba(201,169,110,0.6)' }} />
            {/* Corner marks */}
            {[0, 90, 180, 270].map((deg) => (
              <div
                key={deg}
                className="absolute w-[2.5px] h-[2.5px] bg-[#C9A96E]/50"
                style={{
                  transform: `rotate(${deg}deg) translateY(-22px) rotate(45deg)`,
                }}
              />
            ))}
          </div>

          {/* Establishment line */}
          <p
            className="font-serif px-2"
            style={{
              fontSize: '9px',
              letterSpacing: '0.32em',
              fontFamily: '"Playfair Display", Georgia, serif',
              fontStyle: 'italic',
              color: '#C9A96E',
              opacity: 0.95,
              textShadow: '0 0 24px rgba(201,169,110,0.35)',
            }}
          >
            Nairobi &nbsp;·&nbsp; New York &nbsp;·&nbsp; Washington DC
          </p>
        </div>

        {/* ANTEROOM — commanding centerpiece */}
        <div
          style={{
            opacity: loaded ? 1 : 0,
            transform: loaded ? 'translateY(0)' : 'translateY(36px)',
            transition: 'opacity 1.4s ease 0.6s, transform 1.4s ease 0.6s',
          }}
          className="w-full"
        >
          <h1
            className="text-white font-serif w-full px-2"
            style={{
              fontSize: 'clamp(48px, 14vw, 200px)',
              fontWeight: 400,
              letterSpacing: '0.14em',
              lineHeight: 0.88,
              fontFamily: '"Playfair Display", Georgia, serif',
              textShadow: '0 2px 80px rgba(0,0,0,0.6), 0 0 120px rgba(201,169,110,0.08)',
            }}
          >
            ANTEROOM
          </h1>
        </div>

        {/* Ornamental gold rule — upgraded */}
        <div
          className="w-full flex items-center justify-center mt-5 md:mt-7 mb-6 md:mb-8"
          style={{
            opacity: loaded ? 1 : 0,
            transition: 'opacity 1s ease 1.1s',
          }}
        >
          <div
            className="h-[1px] bg-gradient-to-r from-transparent via-[#C9A96E]/60 to-[#C9A96E]"
            style={{ width: loaded ? 'clamp(120px, 30vw, 220px)' : '0px', transition: 'width 1.4s ease 1.2s' }}
          />
          <div className="flex items-center gap-[5px] md:gap-[7px] mx-4 md:mx-6 flex-shrink-0">
            <div className="w-[2.5px] md:w-[3px] h-[2.5px] md:h-[3px] bg-[#C9A96E]/50 rotate-45" />
            <div className="w-[1px] h-[12px] md:h-[14px] bg-[#C9A96E]/40" />
            <div className="w-[7px] md:w-[8px] h-[7px] md:h-[8px] bg-[#C9A96E] rotate-45" style={{ boxShadow: '0 0 10px rgba(201,169,110,0.5)' }} />
            <div className="w-[1px] h-[12px] md:h-[14px] bg-[#C9A96E]/40" />
            <div className="w-[2.5px] md:w-[3px] h-[2.5px] md:h-[3px] bg-[#C9A96E]/50 rotate-45" />
          </div>
          <div
            className="h-[1px] bg-gradient-to-l from-transparent via-[#C9A96E]/60 to-[#C9A96E]"
            style={{ width: loaded ? 'clamp(120px, 30vw, 220px)' : '0px', transition: 'width 1.4s ease 1.2s' }}
          />
        </div>

        {/* Sub-logo — three-tier sovereign identity */}
        <div
          style={{
            opacity: loaded ? 1 : 0,
            transform: loaded ? 'translateY(0)' : 'translateY(16px)',
            transition: 'opacity 1.1s ease 1.4s, transform 1.1s ease 1.4s',
          }}
          className="flex flex-col items-center gap-[8px] md:gap-[10px] mb-8 md:mb-10 px-4"
        >
          {/* Tier 1 — primary italic tagline */}
          <p
            className="text-white/95 font-serif"
            style={{
              fontSize: 'clamp(16px, 2.1vw, 26px)',
              letterSpacing: '0.06em',
              fontStyle: 'italic',
              fontFamily: '"Playfair Display", Georgia, serif',
              textShadow: '0 2px 24px rgba(0,0,0,0.5)',
            }}
          >
            Where Power Is Prepared.
          </p>

          {/* Tier 2 — gold spaced caps */}
          <p
            className="text-[#C9A96E] font-sans uppercase"
            style={{ fontSize: 'clamp(8px, 1.5vw, 9.5px)', letterSpacing: '0.38em', fontWeight: 400 }}
          >
            Africa&apos;s Premier Sovereign Advisory Firm
          </p>

          {/* Tier 3 — whisper clientele line */}
          <p
            className="text-white/32 font-sans uppercase mt-[2px] hidden md:block"
            style={{ fontSize: '7.5px', letterSpacing: '0.34em' }}
          >
            Heads of State &nbsp;·&nbsp; Cabinet Ministers &nbsp;·&nbsp; Parliamentarians &nbsp;·&nbsp; Business Elites
          </p>
        </div>

        {/* CTA */}
        <a
          href="#contact"
          onClick={(e) => {
            e.preventDefault();
            document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
          }}
          className="group inline-flex items-center justify-center gap-3 md:gap-5 border border-[#C9A96E]/60 text-[#C9A96E] font-sans uppercase px-8 md:px-14 py-4 md:py-5 hover:bg-[#C9A96E] hover:text-[#0D0C0A] transition-all duration-500 whitespace-nowrap cursor-pointer w-full md:w-auto max-w-[90%] md:max-w-none"
          style={{
            fontSize: 'clamp(7.5px, 1.5vw, 8.5px)',
            letterSpacing: '0.28em',
            opacity: loaded ? 1 : 0,
            transform: loaded ? 'translateY(0)' : 'translateY(16px)',
            transition: 'opacity 1s ease 1.9s, transform 1s ease 1.9s, background-color 0.5s, color 0.5s',
          }}
        >
          <span className="text-center">Request a Confidential Consultation</span>
          <span className="w-[14px] md:w-[18px] h-[1px] bg-current inline-block group-hover:w-[20px] md:group-hover:w-[28px] transition-all duration-500 hidden md:inline-block" />
        </a>
      </div>

      {/* Bottom pillars bar */}
      <div
        className="absolute bottom-0 left-0 right-0 z-20 grid grid-cols-2 md:grid-cols-4 border-t border-white/10"
        style={{
          opacity: loaded ? 1 : 0,
          transform: loaded ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 1s ease 2.2s, transform 1s ease 2.2s',
          backgroundColor: 'rgba(13,12,10,0.78)',
          backdropFilter: 'blur(14px)',
        }}
      >
        {pillars.map((p, i) => (
          <div
            key={i}
            className="flex flex-col items-center justify-center py-5 md:py-7 border-r border-white/10 last:border-r-0 md:last:border-r-0 border-b md:border-b-0 group cursor-pointer hover:bg-white/5 transition-colors duration-400"
            style={{
              borderBottom: i < 2 ? '1px solid rgba(255,255,255,0.1)' : 'none',
            }}
            onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
          >
            <span
              className="text-white/90 font-serif mb-1 group-hover:text-[#C9A96E] transition-colors duration-400 text-center px-2"
              style={{ fontSize: 'clamp(11px, 2vw, 14px)', letterSpacing: '0.04em', fontFamily: '"Playfair Display", Georgia, serif' }}
            >
              {p.label}
            </span>
            <span
              className="text-white/35 font-sans uppercase text-center px-2"
              style={{ fontSize: 'clamp(6.5px, 1.2vw, 7.5px)', letterSpacing: '0.24em' }}
            >
              {p.sub}
            </span>
          </div>
        ))}
      </div>

      {/* Scroll indicator */}
      <div
        className="absolute bottom-[120px] right-[60px] flex-col items-center gap-3 hidden md:flex"
        style={{
          opacity: loaded ? 0.4 : 0,
          transition: 'opacity 1s ease 2.5s',
        }}
      >
        <span
          className="text-white/45 font-sans uppercase"
          style={{ fontSize: '7.5px', letterSpacing: '0.24em', writingMode: 'vertical-rl' }}
        >
          Scroll
        </span>
        <div className="w-[1px] h-[56px] bg-white/25 animate-pulse" />
      </div>
    </section>
  );
});

Hero.displayName = 'Hero';

export default Hero;